import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { adminApi } from "../../../lib/admin";
import type {
  AdminReviewSummaryResponse,
  AdminTeenCompletedGigResponse,
  AdminUserEmailModerationResponse,
  AdminUserGuardianLinkResponse,
} from "../../../types/adminTypes";
import { COMPANY_EMAIL } from "../../../constants/company";
import { apiErrorToMessage } from "../../../utils/ApiUtils";
import { formatDateTime, formatDateTimeInGigTimeZone } from "../../../utils/DateUtils";
import { formatGigCompensation } from "../../../utils/GigCompensationUtils";
import { formatCityStateZip } from "../../../utils/LocationUtils";
import Button from "../../ui/Button/Button";
import Modal from "../../ui/Modal/Modal";
import AdminPillBadge from "../ui/AdminPillBadge";

export type AdminUserDetailModalUserRef = {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  status?: "PENDING" | "ACTIVE" | "SUSPENDED" | "DELETED";
  deletedAt?: string | null;
};

type AdminUserDetailModalProps = {
  user: AdminUserDetailModalUserRef | null;
  isOpen: boolean;
  canWrite: boolean;
  onClose: () => void;
  onOpenUser: (user: AdminUserDetailModalUserRef) => void;
};

const getGuardianRelationshipSectionTitle = (roles: string[]) => {
  const hasTeenRole = roles.includes("TEEN");
  const hasAdultRole = roles.includes("ADULT");

  if (hasTeenRole && !hasAdultRole) {
    return "Parent/guardian linked accounts";
  }
  if (hasAdultRole && !hasTeenRole) {
    return "Linked teen accounts";
  }
  return "Guardian relationships";
};

const getGuardianRelationshipEmptyMessage = (roles: string[]) => {
  const hasTeenRole = roles.includes("TEEN");
  const hasAdultRole = roles.includes("ADULT");

  if (hasTeenRole && !hasAdultRole) {
    return "No parent or guardian accounts linked yet.";
  }
  if (hasAdultRole && !hasTeenRole) {
    return "No teen accounts linked yet.";
  }
  return "No guardian relationships yet.";
};

const getGuardianRelationshipCountLabel = (roles: string[]) => {
  const hasTeenRole = roles.includes("TEEN");
  const hasAdultRole = roles.includes("ADULT");

  if (hasTeenRole && !hasAdultRole) {
    return "Linked guardians";
  }
  if (hasAdultRole && !hasTeenRole) {
    return "Linked teens";
  }
  return "Linked accounts";
};

const getGuardianLinkTone = (link: AdminUserGuardianLinkResponse) => (
  link.relationshipStatus === "ACTIVE" ? "success" : "warning"
);

const getGuardianLinkDisplayName = (link: AdminUserGuardianLinkResponse) => {
  const fullName = `${link.relatedUserFirstName} ${link.relatedUserLastName}`.trim();
  return fullName || "Unnamed account";
};

const getReviewContextLabel = (reviewContext: AdminReviewSummaryResponse["reviewContext"]) => (
  reviewContext === "WORKER_PERFORMANCE" ? "Worker performance" : "Poster experience"
);

const getReviewCounterpartyName = (review: AdminReviewSummaryResponse) => {
  const fullName = `${review.reviewerFirstName ?? ""} ${review.reviewerLastName ?? ""}`.trim();
  return fullName || "Unknown reviewer";
};

const getCompletedGigPosterName = (gig: AdminTeenCompletedGigResponse) => {
  const fullName = `${gig.posterFirstName ?? ""} ${gig.posterLastName ?? ""}`.trim();
  if (fullName && gig.posterEmail) {
    return `${fullName} • ${gig.posterEmail}`;
  }
  return fullName || gig.posterEmail || "Unknown poster";
};

const getReviewSummaryKey = (review: AdminReviewSummaryResponse, index: number) => [
  review.createdAt,
  review.gigTitle ?? "gig",
  review.reviewerEmail ?? getReviewCounterpartyName(review),
  review.revieweeEmail ?? "reviewee",
  review.reviewContext,
  review.rating,
  index,
].join("|");

const getAgeLabel = (dateOfBirth: string | null) => {
  if (!dateOfBirth) {
    return "Unknown";
  }

  const birthDate = new Date(`${dateOfBirth}T00:00:00`);
  if (Number.isNaN(birthDate.getTime())) {
    return "Unknown";
  }

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDelta = today.getMonth() - birthDate.getMonth();

  if (monthDelta < 0 || (monthDelta === 0 && today.getDate() < birthDate.getDate())) {
    age -= 1;
  }

  return `${age}`;
};

const isDeletedEmail = (email?: string | null) => (
  Boolean(email?.toLowerCase().endsWith("@deleted.myteengig.local"))
);

const getAdminUserDisplayName = (user: { firstName: string; lastName: string; status?: string }) => (
  user.status === "DELETED" ? "Deleted account" : `${user.firstName} ${user.lastName}`.trim() || "Unnamed account"
);

const getAdminUserDisplayEmail = (email?: string | null, status?: string) => (
  status === "DELETED" || isDeletedEmail(email) ? "Account email removed" : email || "No email provided"
);

const getModerationTone = (emailModeration: AdminUserEmailModerationResponse) => {
  switch (emailModeration.state) {
    case "BANNED":
      return "danger";
    case "BAN_PENDING":
    case "UNBAN_PENDING":
      return "warning";
    default:
      return "success";
  }
};

const getModerationLabel = (emailModeration: AdminUserEmailModerationResponse) => {
  switch (emailModeration.state) {
    case "BAN_PENDING":
      return "Ban Pending";
    case "BANNED":
      return "Banned";
    case "UNBAN_PENDING":
      return "Unban Pending";
    default:
      return "Clear";
  }
};

const getTeenPayoutTone = (isReady: boolean): "success" | "warning" => (
  isReady ? "success" : "warning"
);

type PendingSupportAction = "resendVerification" | "sendPasswordReset" | null;

const AdminUserDetailModalContent = ({ user, isOpen, canWrite, onClose, onOpenUser }: AdminUserDetailModalProps) => {
  const queryClient = useQueryClient();
  const [resendReason, setResendReason] = useState("");
  const [resendFeedback, setResendFeedback] = useState<string | null>(null);
  const [passwordResetReason, setPasswordResetReason] = useState("");
  const [passwordResetFeedback, setPasswordResetFeedback] = useState<string | null>(null);
  const [moderationReason, setModerationReason] = useState("");
  const [moderationFeedback, setModerationFeedback] = useState<string | null>(null);
  const [pendingSupportAction, setPendingSupportAction] = useState<PendingSupportAction>(null);

  const userDetailQuery = useQuery({
    queryKey: ["admin-user-detail", user?.id],
    queryFn: () => adminApi.getUserDetail(user!.id),
    enabled: isOpen && user !== null,
  });

  const teenCompletedGigsQuery = useQuery({
    queryKey: ["admin-user-teen-completed-gigs", user?.id],
    queryFn: () => adminApi.listTeenCompletedGigs(user!.id),
    enabled: false,
  });

  const resendMutation = useMutation({
    mutationFn: () => adminApi.resendVerificationEmail(user!.id, { reason: resendReason.trim() }),
    onSuccess: async () => {
      setResendFeedback("Verification email re-queued.");
      setResendReason("");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["admin-users"] }),
        queryClient.invalidateQueries({ queryKey: ["admin-user-detail", user?.id] }),
        queryClient.invalidateQueries({ queryKey: ["admin-notifications"] }),
        queryClient.invalidateQueries({ queryKey: ["admin-audit"] }),
      ]);
    },
    onError: (error) => {
      setResendFeedback(apiErrorToMessage(error, "Unable to resend verification email."));
    },
  });

  const passwordResetMutation = useMutation({
    mutationFn: () => adminApi.sendPasswordReset(user!.id, { reason: passwordResetReason.trim() }),
    onSuccess: async () => {
      setPasswordResetFeedback("Password reset link sent.");
      setPasswordResetReason("");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["admin-users"] }),
        queryClient.invalidateQueries({ queryKey: ["admin-user-detail", user?.id] }),
        queryClient.invalidateQueries({ queryKey: ["admin-notifications"] }),
        queryClient.invalidateQueries({ queryKey: ["admin-audit"] }),
      ]);
    },
    onError: (error) => {
      setPasswordResetFeedback(apiErrorToMessage(error, "Unable to send password reset link."));
    },
  });

  const moderationMutation = useMutation({
    mutationFn: (action: "ban" | "unban") => (
      action === "ban"
        ? adminApi.requestEmailBan(user!.id, { reason: moderationReason.trim() })
        : adminApi.requestEmailUnban(user!.id, { reason: moderationReason.trim() })
    ),
    onSuccess: async (_, action) => {
      setModerationFeedback(
        action === "ban"
          ? `Ban request submitted. Approval email sent to ${COMPANY_EMAIL}.`
          : `Unban request submitted. Approval email sent to ${COMPANY_EMAIL}.`,
      );
      setModerationReason("");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["admin-users"] }),
        queryClient.invalidateQueries({ queryKey: ["admin-user-detail", user?.id] }),
        queryClient.invalidateQueries({ queryKey: ["admin-notifications"] }),
        queryClient.invalidateQueries({ queryKey: ["admin-audit"] }),
      ]);
    },
    onError: (error) => {
      setModerationFeedback(apiErrorToMessage(error, "Unable to submit moderation request."));
    },
  });

  const detail = userDetailQuery.data;
  const emailModeration = detail?.emailModeration;
  const isDeletedAccount = detail?.status === "DELETED";
  const canRequestBan = !isDeletedAccount && emailModeration?.state === "CLEAR";
  const canRequestUnban = !isDeletedAccount && emailModeration?.state === "BANNED";
  const supportActionPending = resendMutation.isPending || passwordResetMutation.isPending;

  const confirmSupportAction = () => {
    if (pendingSupportAction === "resendVerification") {
      resendMutation.mutate();
      setPendingSupportAction(null);
      return;
    }
    if (pendingSupportAction === "sendPasswordReset") {
      passwordResetMutation.mutate();
      setPendingSupportAction(null);
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title={user ? getAdminUserDisplayName(user) : "User detail"} panelClassName="max-w-3xl">
      {userDetailQuery.isLoading ? <p className="text-sm text-myteengig-text-muted">Loading user detail...</p> : null}
      {userDetailQuery.error ? <p className="text-sm text-red-600">{apiErrorToMessage(userDetailQuery.error, "Failed to load user detail.")}</p> : null}

      {detail ? (
        <div className="space-y-6">
          {isDeletedAccount ? (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-4">
              <p className="text-sm font-semibold text-rose-800">Deleted account</p>
              <p className="mt-2 text-sm leading-6 text-rose-700">
                This account has been closed and direct profile/contact details were scrubbed. Historical gig, review, payment, and safety records remain available for support and audit context.
              </p>
            </div>
          ) : null}

          {isDeletedAccount ? (
            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-sm font-semibold text-myteengig-text-primary">Deletion audit</p>
              <div className="mt-3 grid gap-3 text-sm text-myteengig-text-muted md:grid-cols-2">
                <p>User ID: {detail.id}</p>
                <p>Source: {detail.deletionAudit?.deletionSource ?? "Unknown"}</p>
                <p>Deleted: {formatDateTime(detail.deletionAudit?.deletedAt ?? detail.deletedAt)}</p>
                <p>Created: {formatDateTime(detail.deletionAudit?.accountCreatedAt ?? detail.createdAt)}</p>
                <p>
                  Former name: {[detail.deletionAudit?.firstName, detail.deletionAudit?.lastName].filter(Boolean).join(" ") || "Not available"}
                </p>
                <p>Former email: {detail.deletionAudit?.email ?? "Not available"}</p>
              </div>
              {detail.deletionAudit?.reason ? (
                <p className="mt-4 whitespace-pre-wrap break-words text-sm text-myteengig-text-muted">
                  Reason for leaving: {detail.deletionAudit.reason}
                </p>
              ) : null}
              <div className="mt-4 flex flex-wrap gap-2">
                {(detail.deletionAudit?.roles?.length ? detail.deletionAudit.roles : detail.roles).map((role) => (
                  <AdminPillBadge key={role}>{role}</AdminPillBadge>
                ))}
              </div>
            </div>
          ) : null}

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Status</p>
              <div className="mt-3"><AdminPillBadge tone={isDeletedAccount ? "danger" : "neutral"}>{detail.status}</AdminPillBadge></div>
              {detail.deletedAt ? (
                <p className="mt-2 text-xs text-myteengig-text-muted">Deleted {formatDateTime(detail.deletedAt)}</p>
              ) : null}
            </div>
            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Email</p>
              <p className="mt-3 text-sm text-myteengig-text-primary">
                {isDeletedAccount ? "Removed" : detail.emailVerified ? "Verified" : "Unverified"}
              </p>
            </div>
            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Posted gigs</p>
              <p className="mt-3 text-sm text-myteengig-text-primary">{detail.postedGigCount}</p>
            </div>
            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
                {getGuardianRelationshipCountLabel(detail.roles)}
              </p>
              <p className="mt-3 text-sm text-myteengig-text-primary">{detail.activeGuardianLinkCount}</p>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-sm font-semibold text-myteengig-text-primary">Profile</p>
              <div className="mt-3 space-y-2 text-sm text-myteengig-text-muted">
                <p>{getAdminUserDisplayEmail(detail.email, detail.status)}</p>
                <p>Account type: {detail.accountType ?? "Unknown"}</p>
                <p>{formatCityStateZip(detail, "No location")}</p>
                <p>Date of birth: {detail.dateOfBirth ?? "Not set"}</p>
                <p>Age: {getAgeLabel(detail.dateOfBirth)}</p>
                <p>Created: {formatDateTime(detail.createdAt)}</p>
                <p>Updated: {formatDateTime(detail.updatedAt)}</p>
              </div>
            </div>
            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-sm font-semibold text-myteengig-text-primary">Roles and skills</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {detail.roles.map((role) => <AdminPillBadge key={role}>{role}</AdminPillBadge>)}
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {detail.skills.length > 0
                  ? detail.skills.map((skill) => <AdminPillBadge key={skill}>{skill}</AdminPillBadge>)
                  : <p className="text-sm text-myteengig-text-muted">No skills listed.</p>}
              </div>
            </div>
          </div>

          {detail.roles.includes("TEEN") ? (
            <div className="rounded-2xl border border-myteengig-border p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm font-semibold text-myteengig-text-primary">Teen Stripe payouts</p>
                <AdminPillBadge tone={getTeenPayoutTone(detail.teenPayoutReady)}>
                  {detail.teenPayoutReady ? "Ready" : "Not ready"}
                </AdminPillBadge>
              </div>
              <div className="mt-3 grid gap-3 text-sm text-myteengig-text-muted sm:grid-cols-2">
                <p>Owner: {detail.teenPayoutOwnerType ?? "Not set"}</p>
                <p>Status: {detail.teenPayoutOnboardingStatus ?? "No profile"}</p>
                <p>Payouts enabled: {detail.teenPayoutsEnabled ? "Yes" : "No"}</p>
                <p>Stripe account: {detail.teenStripeConnectedAccountPresent ? "Connected" : "Not connected"}</p>
              </div>
            </div>
          ) : null}

          {detail.roles.includes("TEEN") ? (
            <div className="rounded-2xl border border-myteengig-border p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-myteengig-text-primary">Completed gigs</p>
                  <p className="mt-2 text-sm text-myteengig-text-muted">
                    Load this teen&apos;s recent completed gig history for support review.
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {teenCompletedGigsQuery.data ? <AdminPillBadge>{teenCompletedGigsQuery.data.length}</AdminPillBadge> : null}
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={teenCompletedGigsQuery.isFetching}
                    onClick={() => {
                      void teenCompletedGigsQuery.refetch();
                    }}
                  >
                    {teenCompletedGigsQuery.isFetching
                      ? "Loading..."
                      : teenCompletedGigsQuery.data
                        ? "Refresh List"
                        : "Load Completed Gigs"}
                  </Button>
                </div>
              </div>

              {teenCompletedGigsQuery.error ? (
                <p className="mt-3 text-sm text-red-600">
                  {apiErrorToMessage(teenCompletedGigsQuery.error, "Failed to load completed gigs.")}
                </p>
              ) : null}

              {teenCompletedGigsQuery.data ? (
                teenCompletedGigsQuery.data.length > 0 ? (
                  <div className="mt-4 grid gap-3">
                    {teenCompletedGigsQuery.data.length === 50 ? (
                      <p className="text-xs text-myteengig-text-muted">Showing the 50 most recent completed gigs.</p>
                    ) : null}
                    {teenCompletedGigsQuery.data.map((gig) => (
                      <div key={gig.requestId} className="rounded-2xl border border-myteengig-border bg-myteengig-background/50 px-4 py-3">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="break-words text-sm font-semibold text-myteengig-text-primary">{gig.gigTitle ?? "Untitled gig"}</p>
                            <p className="mt-1 break-words text-xs text-myteengig-text-muted">
                              Poster: {getCompletedGigPosterName(gig)}
                            </p>
                          </div>
                          <AdminPillBadge>{formatGigCompensation(gig.paymentAmountCents, gig.volunteer)}</AdminPillBadge>
                        </div>
                        <div className="mt-3 grid gap-2 text-sm text-myteengig-text-muted sm:grid-cols-2">
                          <p>Category: {gig.category ?? "Not set"}</p>
                          <p>Location: {formatCityStateZip(gig, "No location")}</p>
                          <p>
                            Scheduled: {formatDateTimeInGigTimeZone(gig.confirmedStartTime, gig.gigTimeZone)} to {formatDateTimeInGigTimeZone(gig.confirmedEndTime, gig.gigTimeZone)}
                          </p>
                          <p>Completed: {formatDateTimeInGigTimeZone(gig.completedAt, gig.gigTimeZone, "Not available")}</p>
                          <p>Teen marked complete: {formatDateTime(gig.teenMarkedCompletedAt, "Not available")}</p>
                          <p>Poster marked complete: {formatDateTime(gig.adultConfirmedCompletedAt, "Not available")}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="mt-3 text-sm text-myteengig-text-muted">No completed gigs found for this teen.</p>
                )
              ) : null}
            </div>
          ) : null}

          <div className="rounded-2xl border border-myteengig-border p-4">
            <p className="text-sm font-semibold text-myteengig-text-primary">Bio</p>
            <p className="mt-3 text-sm text-myteengig-text-muted">{detail.bio ?? "No bio provided."}</p>
          </div>

          {detail.accountType === "BUSINESS" ? (
            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-sm font-semibold text-myteengig-text-primary">Business profile</p>
              <div className="mt-3 space-y-2 text-sm text-myteengig-text-muted">
                <p>{detail.businessName ?? "No business name provided."}</p>
                <p>{detail.businessWebsite ?? "No business website provided."}</p>
                <p className="whitespace-pre-wrap">{detail.businessDescription ?? "No business description provided."}</p>
              </div>
            </div>
          ) : null}

          <div className="rounded-2xl border border-myteengig-border p-4">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-semibold text-myteengig-text-primary">Received reviews</p>
              <AdminPillBadge>{detail.receivedReviews.length}</AdminPillBadge>
            </div>
            {detail.receivedReviews.length > 0 ? (
              <div className="mt-4 grid gap-3">
                {detail.receivedReviews.map((review, index) => (
                  <div key={getReviewSummaryKey(review, index)} className="rounded-2xl border border-myteengig-border bg-myteengig-background/50 px-4 py-3">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-myteengig-text-primary">{getReviewCounterpartyName(review)}</p>
                        <p className="mt-1 text-xs text-myteengig-text-muted">
                          {review.gigTitle ?? "Gig"} • {getReviewContextLabel(review.reviewContext)} • {formatDateTime(review.createdAt)}
                        </p>
                      </div>
                      <AdminPillBadge>{review.rating.toFixed(1)} / 5</AdminPillBadge>
                    </div>
                    <p className="mt-3 whitespace-pre-wrap break-words text-sm text-myteengig-text-primary">
                      {review.message?.trim() ? review.message : "No written feedback was included with this review."}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-3 text-sm text-myteengig-text-muted">No reviews received yet.</p>
            )}
          </div>

          <div className="rounded-2xl border border-myteengig-border p-4">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-semibold text-myteengig-text-primary">
                {getGuardianRelationshipSectionTitle(detail.roles)}
              </p>
              <AdminPillBadge>{detail.guardianLinks.length}</AdminPillBadge>
            </div>
            {detail.guardianLinks.length > 0 ? (
              <div className="mt-4 grid gap-3">
                {detail.guardianLinks.map((link) => (
                  <button
                    key={link.relatedUserId}
                    type="button"
                    onClick={() => onOpenUser({
                      id: link.relatedUserId,
                      firstName: link.relatedUserFirstName,
                      lastName: link.relatedUserLastName,
                      email: link.relatedUserEmail,
                    })}
                    className="flex items-center justify-between gap-4 rounded-2xl border border-myteengig-border bg-myteengig-background/50 px-4 py-3 text-left transition hover:border-myteengig-primary hover:bg-white"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-myteengig-text-primary">
                        {getGuardianLinkDisplayName(link)}
                      </p>
                      <p className="mt-1 truncate text-sm text-myteengig-text-muted">
                        {link.relatedUserEmail || "No email provided"}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-3">
                      <AdminPillBadge tone={getGuardianLinkTone(link)}>{link.statusLabel}</AdminPillBadge>
                      <span className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Open</span>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <p className="mt-3 text-sm text-myteengig-text-muted">
                {getGuardianRelationshipEmptyMessage(detail.roles)}
              </p>
            )}
          </div>

          {emailModeration ? (
            <div className="rounded-2xl border border-dashed border-myteengig-border p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-myteengig-text-primary">Email moderation</p>
                  <p className="mt-2 text-sm text-myteengig-text-muted">
                    Ban prevents this email from signing up, logging in, resetting a password, or re-verifying until an approved unban request is completed.
                  </p>
                </div>
                <AdminPillBadge tone={getModerationTone(emailModeration)}>
                  {getModerationLabel(emailModeration)}
                </AdminPillBadge>
              </div>

              {emailModeration.activeReason ? (
                <p className="mt-4 text-sm text-myteengig-text-muted">
                  Active ban reason: {emailModeration.activeReason}
                </p>
              ) : null}
              {emailModeration.pendingRequestedAt ? (
                <p className="mt-2 text-sm text-myteengig-text-muted">
                  Pending {emailModeration.pendingAction?.toLowerCase()} requested {formatDateTime(emailModeration.pendingRequestedAt)}.
                </p>
              ) : null}

              {canRequestBan || canRequestUnban ? (
                <>
                  <label htmlFor="admin-user-moderation-reason" className="mt-4 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
                    Reason
                  </label>
                  <textarea
                    id="admin-user-moderation-reason"
                    value={moderationReason}
                    onChange={(event) => setModerationReason(event.target.value)}
                    rows={3}
                    className="mt-2 w-full rounded-lg border border-myteengig-border bg-white px-4 py-3 text-sm text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
                    placeholder={
                      canRequestBan
                        ? "Document why this email should be banned."
                        : "Document why this email ban should be lifted."
                    }
                  />
                </>
              ) : null}

              {moderationFeedback ? <p className="mt-3 text-sm text-myteengig-text-muted">{moderationFeedback}</p> : null}

              <div className="mt-4 flex justify-end">
                {canRequestBan ? (
                  <Button
                    disabled={!canWrite || moderationReason.trim().length === 0 || moderationMutation.isPending}
                    onClick={() => moderationMutation.mutate("ban")}
                  >
                    {moderationMutation.isPending ? "Submitting..." : "Request Email Ban"}
                  </Button>
                ) : null}
                {canRequestUnban ? (
                  <Button
                    disabled={!canWrite || moderationReason.trim().length === 0 || moderationMutation.isPending}
                    onClick={() => moderationMutation.mutate("unban")}
                  >
                    {moderationMutation.isPending ? "Submitting..." : "Request Email Unban"}
                  </Button>
                ) : null}
              </div>
            </div>
          ) : null}

          {!isDeletedAccount && !detail.emailVerified ? (
            <div className="rounded-2xl border border-dashed border-myteengig-border p-4">
              <p className="text-sm font-semibold text-myteengig-text-primary">Support action</p>
              <p className="mt-2 text-sm text-myteengig-text-muted">
                Resend the verification email only when you have a support reason. This action is written to the admin audit log.
              </p>
              <label htmlFor="admin-user-resend-reason" className="mt-4 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
                Reason
              </label>
              <textarea
                id="admin-user-resend-reason"
                value={resendReason}
                onChange={(event) => setResendReason(event.target.value)}
                rows={3}
                className="mt-2 w-full rounded-lg border border-myteengig-border bg-white px-4 py-3 text-sm text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
                placeholder="User reported they never received the original verification email."
              />
              {resendFeedback ? <p className="mt-3 text-sm text-myteengig-text-muted">{resendFeedback}</p> : null}
              <div className="mt-4 flex justify-end">
                <Button
                  disabled={!canWrite || resendReason.trim().length === 0 || supportActionPending}
                  onClick={() => setPendingSupportAction("resendVerification")}
                >
                  {resendMutation.isPending ? "Resending..." : "Resend Verification Email"}
                </Button>
              </div>
            </div>
          ) : null}

          {!isDeletedAccount ? (
          <div className="rounded-2xl border border-dashed border-myteengig-border p-4">
            <p className="text-sm font-semibold text-myteengig-text-primary">Password reset</p>
            <p className="mt-2 text-sm text-myteengig-text-muted">
              Send a password reset link to this user when they cannot access their account. This action is written to the admin audit log.
            </p>
            <label htmlFor="admin-user-password-reset-reason" className="mt-4 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
              Reason
            </label>
            <textarea
              id="admin-user-password-reset-reason"
              value={passwordResetReason}
              onChange={(event) => setPasswordResetReason(event.target.value)}
              rows={3}
              className="mt-2 w-full rounded-lg border border-myteengig-border bg-white px-4 py-3 text-sm text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
              placeholder="User reported they cannot sign in and needs a reset link."
            />
            {passwordResetFeedback ? <p className="mt-3 text-sm text-myteengig-text-muted">{passwordResetFeedback}</p> : null}
            <div className="mt-4 flex justify-end">
              <Button
                disabled={!canWrite || passwordResetReason.trim().length === 0 || supportActionPending}
                onClick={() => setPendingSupportAction("sendPasswordReset")}
              >
                {passwordResetMutation.isPending ? "Sending..." : "Send Password Reset Link"}
              </Button>
            </div>
          </div>
          ) : null}
        </div>
      ) : null}
      </Modal>
      <Modal
        isOpen={pendingSupportAction !== null}
        onClose={() => {
          if (!supportActionPending) {
            setPendingSupportAction(null);
          }
        }}
        title={pendingSupportAction === "sendPasswordReset" ? "Send Password Reset Link?" : "Resend Verification Email?"}
        panelClassName="max-w-lg"
      >
        <div className="space-y-4">
          <div className="rounded-2xl bg-myteengig-background px-4 py-4">
            <p className="text-sm font-semibold text-myteengig-text-primary">
              {getAdminUserDisplayEmail(detail?.email ?? user?.email, detail?.status ?? user?.status)}
            </p>
            <p className="mt-2 text-sm text-myteengig-text-muted">
              {pendingSupportAction === "sendPasswordReset"
                ? "This will send a password reset link to the user."
                : "This will resend the email verification link to the user."}
            </p>
          </div>
          <p className="text-sm text-myteengig-text-muted">
            {pendingSupportAction === "sendPasswordReset"
              ? "Continue with sending the password reset email?"
              : "Continue with resending the verification email?"}
          </p>
          <div className="flex flex-wrap justify-end gap-3">
            <Button
              variant="outline"
              disabled={supportActionPending}
              onClick={() => setPendingSupportAction(null)}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              disabled={supportActionPending}
              onClick={confirmSupportAction}
              className="w-full sm:w-auto"
            >
              {pendingSupportAction === "sendPasswordReset"
                ? (passwordResetMutation.isPending ? "Sending..." : "Yes, Send Reset Link")
                : (resendMutation.isPending ? "Resending..." : "Yes, Resend Email")}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

const AdminUserDetailModal = (props: AdminUserDetailModalProps) => (
  <AdminUserDetailModalContent key={props.user?.id ?? "no-user"} {...props} />
);

export default AdminUserDetailModal;
